import { Button, Input, Space, Tag, InputRef } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { NotificationAlert } from '../../components';
import {
  ExpectedSkillLevel,
  RolesBasedResponsibilities,
} from '../../components/profile';
import SkillMatrixTable from '../../components/profile/SkillMatrixTable';
import {
  CompetencyItem,
  TierDisciplineResponsibilitiesItems,
} from '../../types/competenciesTypes';
import { getCompetencies } from '../../services/competenciesService';
import { IExpectedSkillLevelProps } from '../../components/profile/ExpectedSkillLevel';
import { disciplineBasedRolesIcons } from '../../helper/constants';
import {
  addUpdateUserInterests,
  getAllUserInterests,
  UserInterestType,
} from '../../services/skillMatrixService';
import DomainRating from './DomainRating';
import Certification from './Certification';

interface IProps {
  userId: string;
  tier: string;
  designation: string;
  type: 'hr' | 'mentor' | 'mentee';
}

function UserProfile({ type, userId, designation, tier }: IProps) {
  const myRef = useRef<any>();
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const [competency, setCompetency] = useState<CompetencyItem[]>([]);
  const [tierDisciplineResponsibilities, setTierDisciplineResponsibilities] =
    useState<TierDisciplineResponsibilitiesItems[]>([]);
  const [currentInterests, setCurrentInterests] = useState<UserInterestType[]>(
    []
  );

  const isReadOnlyMode = type != 'mentee';

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    getCompetencies(userId, 'profile')
      .then((res) => {
        setCompetency(res.data?.competencyItems || []);
        setTierDisciplineResponsibilities(
          res.data?.tierDisciplineResponsibilities || []
        );
      })
      .catch((err) => {
        console.error('err: ', err);
      });

    getAllUserInterests(userId).then(({ data, error }) => {
      if (error) {
        myRef.current?.call('error', error?.message);
        return;
      }
      setCurrentInterests(data ?? []);
    });
  }, [userId]);

  const roleBasedResponsibilitiesData = useMemo(() => {
    const data: {
      icon: string;
      descriptions: string[];
      statementIds: number[];
    }[] = [];

    tierDisciplineResponsibilities.forEach((com) => {
      const existingRec = disciplineBasedRolesIcons.find((dis) =>
        dis.statementIds.includes(com.id)
      );

      if (existingRec) {
        const existingFromDataIndex = data.findIndex(
          (d) => d.icon === existingRec.icon
        );

        if (existingFromDataIndex > -1) {
          // Add description to existing record
          data[existingFromDataIndex].descriptions.push(com.description);
          data[existingFromDataIndex].statementIds.push(com.id);
        } else {
          // Add as new record
          data.push({
            icon: existingRec.icon,
            descriptions: [com.description],
            statementIds: [com.id],
          });
        }
      }
    });

    return data;
  }, [tierDisciplineResponsibilities]);

  const handleClose = (removedTag: string) => {
    const newTags = currentInterests.filter(
      (tag) => tag.interests !== removedTag
    );
    setCurrentInterests(newTags);
    addUpdateUserInterests(
      newTags.map((n) => ({
        UserId: userId,
        Interest: n.interests,
      }))
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (
      inputValue &&
      !currentInterests.find((i) => i.interests === inputValue)
    ) {
      addUpdateUserInterests([
        ...currentInterests.map((n) => ({
          UserId: userId,
          Interest: n.interests,
        })),
        {
          UserId: userId,
          Interest: inputValue,
        },
      ]).then(({ data }) => {
        setCurrentInterests([...data]);
      });
    }
    setInputVisible(false);
    setInputValue('');
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const ExpectedSkillLevels: IExpectedSkillLevelProps['data'] = useMemo(() => {
    return competency.map((com) => ({
      skill: com.statement.description,
      level: com.expected.skillLevel.toString(),
      description: com.expected.description,
    }));
  }, [competency]);

  return (
    <>
      <div className="mt-4">
        <RolesBasedResponsibilities
          designation={designation}
          tier={tier}
          data={roleBasedResponsibilitiesData}
        />

        <div className="mt-8">
          <ExpectedSkillLevel
            designation={designation}
            tier={tier}
            data={ExpectedSkillLevels}
          />
        </div>
      </div>

      <div className="mt-8">
        <SkillMatrixTable userId={userId} isDisabledMode={isReadOnlyMode} />
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Current Interests</h2>

          <div className="mt-4 mb-8 border p-4 rounded-lg bg-white shadow-sm">
            <Space size={[0, 8]} wrap className="w-full">
              {currentInterests.length > 0 ? (
                currentInterests.map((tag) => (
                  <Tag
                    key={tag.id}
                    closable={!isReadOnlyMode}
                    onClose={() => handleClose(tag.interests)}
                    className="px-3 py-1 text-base rounded-md"
                  >
                    {tag.interests}
                  </Tag>
                ))
              ) : type === 'mentee' ? (
                <></>
              ) : (
                <span className="text-gray-400 italic">
                  No interests added yet
                </span>
              )}
              {!isReadOnlyMode &&
                (inputVisible ? (
                  <Input
                    ref={inputRef}
                    type="text"
                    size="small"
                    className="w-[100px]"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputConfirm}
                    onPressEnter={handleInputConfirm}
                  />
                ) : (
                  <Button
                    size="small"
                    type="dashed"
                    onClick={showInput}
                    icon={<PlusOutlined />}
                    className="rounded-full"
                  >
                    Add Interest
                  </Button>
                ))}
            </Space>
          </div>
        </div>

        <DomainRating userId={userId} isReadOnlyMode={isReadOnlyMode} />

        <div className="mt-8">
          <Certification userId={userId} isReadOnlyMode={isReadOnlyMode} />
        </div>
      </div>

      <NotificationAlert ref={myRef} />
    </>
  );
}

export default UserProfile;
